import { useRef, useEffect, useMemo, DependencyList } from 'react';
import { debounceImmediate } from '@utils/util';

/**
 * 创建一个防抖版本的回调函数（立即执行版本）
 * 第一次调用立即执行，在等待时间内的后续调用会被忽略
 * 
 * @param callback - 要防抖的回调函数
 * @param delay - 防抖延迟时间（毫秒），默认 300ms
 * @param deps - 依赖数组，当依赖变化时会更新防抖函数
 * @returns 防抖后的回调函数
 * 
 * @example
 * ```typescript
 * const handleClick = useDebouncedCallback(
 *   (id: string) => {
 *     console.log('处理点击:', id);
 *     api.submit(id);
 *   },
 *   1000,
 *   [api] // 依赖项
 * );
 * 
 * // 使用
 * <button onClick={() => handleClick('123')}>点击</button>
 * ```
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
    callback: T,
    delay: number = 300,
    deps: DependencyList = []
): T {
    // 使用 ref 存储最新的 callback
    const callbackRef = useRef(callback);

    // 使用 ref 存储防抖函数
    const debouncedFnRef = useRef<T | null>(null);

    // 当 callback 变化时，更新 ref（保证使用最新的 callback）
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // 当依赖变化时，重新创建防抖函数
    useEffect(() => {
        debouncedFnRef.current = debounceImmediate(
            (...args: any[]) => {
                return callbackRef.current(...args);
            },
            delay
        ) as any;

        // 清理函数：组件卸载时取消防抖
        return () => {
            if (debouncedFnRef.current && typeof (debouncedFnRef.current as any).cancel === 'function') {
                (debouncedFnRef.current as any).cancel();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delay, ...deps]);

    // 返回稳定的函数引用（使用 useMemo 而不是 useCallback）
    return useMemo(
        () => ((...args: Parameters<T>) => {
            return debouncedFnRef.current?.(...args);
        }) as T,
        []
    );
}